import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from "@angular/core";
import { User } from "@core/models";
import { Subject, takeUntil } from "rxjs";
import { Contest, JoinContest } from "../../models/cahoot";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { CahootService } from "../../cahoot.service";
import { ToastrService } from "ngx-toastr";
import { AuthenticationService } from "@core/services/auth";
import { CoreConfigService } from "@core/services/config.service";
import { SignUpModalComponent } from "../sign-up-modal/sign-up-modal.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { HelperFunctionsService } from "@core/services/helper-functions.service";
import { serverErrorMessage } from "@core/helpers";

@Component({
  standalone: false,
  selector: "app-join-via-code",
  templateUrl: "./join-via-code.component.html",
  styleUrls: ["./join-via-code.component.scss"],
})
export class JoinViaCodeComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<boolean>();
  contestId: number;
  form: number;
  currentUser: User;
  title: string;
  contestCreatorId: number;
  queryParams: Contest = {};
  isContestCreator: boolean;
  activeSessions: Contest[];
  joinContestForm: FormGroup;
  isLoadingContest = true;
  isTeacher: boolean;
  contestCode: string;
  difficultLevel: string;
  subject: string;
  topic: string;
  contest: Contest;
  isGettingContestDetails: boolean;
  errorMessage: string;
  tokenId: string;
  showErrorModal: boolean = false;
  submitted: boolean = false;
  isJoiningContest: boolean;
  showErrorMessage: boolean;

  
    private router = inject(Router);
    private cahootService = inject(CahootService);
    private toast = inject(ToastrService);
    private authService = inject(AuthenticationService);
    private fb = inject(FormBuilder);
    private activatedRoute = inject(ActivatedRoute);
    private coreConfigService = inject(CoreConfigService);
    private authenticationService = inject(AuthenticationService);
    private modal = inject(NgbModal);
    private cdr = inject(ChangeDetectorRef);
    private helperService = inject(HelperFunctionsService);

  ngOnInit(): void {
    this.currentUser = this.authService.currentUserValue;
    if (!this.currentUser?.userId) this.configureLayout();

    this.initjoinContestForm();
    this.patchNameField();

      // Pre-fill and auto-lookup when arriving from a Spotlight card
    const codeFromQueryParams = this.activatedRoute.snapshot.queryParams.code;
    if (codeFromQueryParams) {
      this.joinContestForm.get("code").patchValue(codeFromQueryParams);
      this.getContestByCode();
    }
  }

  get f() {
    return this.joinContestForm.get("code");
  }

  configureLayout() {
    this.coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  initjoinContestForm(): void {
    this.joinContestForm = this.fb.group({
      code: ["", Validators.required],
      displayName: ["", Validators.required],
    });
  }

  patchNameField(): void {
    if (this.currentUser?.userId) {
      this.joinContestForm.get("displayName").patchValue(this.currentUser.name);
      this.joinContestForm.get("displayName").disable();
    }
    this.joinContestForm.updateValueAndValidity();
  }

  getContestByCode(): void {
    this.submitted = true;
    const contestCode = this.joinContestForm.get("code").value;
    if (!contestCode) return;

    this.isGettingContestDetails = true;

    this.cahootService
      .getContestByCode(contestCode)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.isGettingContestDetails = false;
          if (res.contestId) {
            this.contest = res;
            this.queryParams = this.buildQueryParams();
          }
        },
        error: (err) => {
          this.isGettingContestDetails = false;
          this.showErrorMessage = true;
          this.errorMessage = err.error.message
            ? err.error.message
            : "Sorry, this contest does not exist Please check with the creator";
        },
      });
  }

   loadSubscribeContest(): void {
    this.helperService.launchModal("unlockMoreLearning");
  }

  buildQueryParams(): Contest {
    return {
      contestId: this.contest?.contestId,
      title: this.contest?.title,
      createdByUserId: this.contest?.createdByUserId,
      isContestCreator:
        this.currentUser?.userId == this.contest?.createdByUserId,
    };
  }

  joinContest(): void {
    this.submitted = true;
    if (!this.joinContestForm.get("code").value) return;

    const payload = this.buildJoinContestPayload();
    this.isJoiningContest = true;

    this.cahootService
      .joinContest(payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.isJoiningContest = false;
          const memberId = res.memberId;
          localStorage.setItem("memberId", memberId);
          localStorage.setItem("userName", res.displayName);

          if (res.token) {
            this.authenticationService
              .setTokens({ access_token: res.token })
              .subscribe(() => {
                this.navigateToWaitingRoom();
              });
            return;
          }

          this.navigateToWaitingRoom();
        },
        error: (err) => {
          this.isJoiningContest = false;
           // prompt user to subscribe
          if (err.status === 402) {
            return this.loadSubscribeContest();
          }
          this.toast.error(
            (this.errorMessage = serverErrorMessage(err, "Couldn't join this contest. Please try again."))
          );
        },
      });
  }

  buildJoinContestPayload(): any {
    const displayName = this.getDisplayName();
    return {
      userId: this.currentUser?.userId ?? null,
      displayName: displayName,
      contestId: this.contest?.contestId,
    };
  }

  getDisplayName(): string {
    return this.currentUser?.userId
      ? this.currentUser.name
      : this.joinContestForm.get("displayName").value;
  }

  navigateToWaitingRoom(): void {
    this.router.navigate(["/beewiz/waiting-room"], {
      queryParams: this.queryParams,
    });
  }

  showJoinContestForm(): void {
    this.showErrorMessage = false;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }
}
